import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import ChatBubble from "./ChatBubble";
import InputBar from "./InputBar";
import InformationForm from "./InformationForm";
import ThinkingIndicator from "./ThinkingIndicator";
import { AnimatePresence, motion } from "framer-motion";
import MESSAGE_SENDER_TYPES from "../constants/message-sender-types";
import conversationService from "../services/conversationService";
import { useInformationForm } from "../hooks/useInformationForm";
import { 
  FOLLOW_UP_INTENTS, 
  PROCESSING_TIMEOUT,
  FOLLOW_UP_UI_TEXT
} from "../constants/follow-up-intents";
import { buildStructuredInputPrompt, getSystemContext, processMessageData } from "../utils/inputPromptBuilder";
import greetingHandler from "../utils/greetingHandler";
import RecentContextPanel from "./RecentContextPanel";
import { useRecentContext } from "../hooks/useRecentContext";
import PropTypes from "prop-types";


const fnCurrentTimeStamp = () => {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const INITIAL_MESSAGE = {
  id: 1,
  sender: MESSAGE_SENDER_TYPES.BOT,
  message: "Hi there! How can I help you?",
  timestamp: fnCurrentTimeStamp(),
};

const ERROR_MESSAGE =
  "I apologize, but I'm having trouble processing your request. Please try again later.";

// Helper functions for sessionStorage management (per tab, per chatMode)
const getDialogIdStorageKey = (chatMode) => `conversation_dialog_id_${chatMode}`;
const getThreadIdStorageKey = (chatMode) => `conversation_thread_id_${chatMode}`;

const loadDialogIdFromStorage = (chatMode) => {
  try {
    const stored = sessionStorage.getItem(getDialogIdStorageKey(chatMode));
    return stored || null;
  } catch (error) {
    console.error('Error loading dialog_id from sessionStorage:', error);
    return null;
  }
};

const saveDialogIdToStorage = (chatMode, dialogId) => {
  try {
    if (dialogId) {
      sessionStorage.setItem(getDialogIdStorageKey(chatMode), dialogId);
    } else {
      sessionStorage.removeItem(getDialogIdStorageKey(chatMode));
    }
  } catch (error) {
    console.error('Error saving dialog_id to sessionStorage:', error);
  }
};

const loadThreadIdFromStorage = (chatMode) => {
  try {
    const stored = sessionStorage.getItem(getThreadIdStorageKey(chatMode));
    return stored || null;
  } catch (error) {
    console.error('Error loading thread_id from sessionStorage:', error);
    return null;
  }
};

const saveThreadIdToStorage = (chatMode, threadId) => {
  try {
    if (threadId) {
      sessionStorage.setItem(getThreadIdStorageKey(chatMode), threadId);
    } else {
      sessionStorage.removeItem(getThreadIdStorageKey(chatMode));
    }
  } catch (error) {
    console.error('Error saving thread_id to sessionStorage:', error);
  }
};

const clearConversationStorage = (chatMode) => {
  try {
    sessionStorage.removeItem(getDialogIdStorageKey(chatMode));
    sessionStorage.removeItem(getThreadIdStorageKey(chatMode));
  } catch (error) {
    console.error('Error clearing conversation storage:', error);
  }
};

export const ChatBot = ({
  chatMode,
  chatModesMap,
  companyCode,
  regionCode,
  userCode,
}) => {
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const chatContainerRef = useRef(null);
  
  // Required for LangGraph conversation continuity
  // Track if we've received a response in this session (after page refresh)
  // This ensures we don't send dialog_id on first request after refresh
  const hasReceivedResponseRef = useRef(false);
  
  // Don't restore dialog_id from sessionStorage on mount - we'll only use it after first response
  const [dialogId, setDialogId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [isFollowupRequired, setIsFollowupRequired] = useState(false);
  
  // Tracks user's follow-up intent per message to maintain context boundaries
  const [messageFollowUpIntents, setMessageFollowUpIntents] = useState({});
  const [isProcessingChoice, setIsProcessingChoice] = useState(false);
  
  // Form management using custom hook
  const {
    showProvideForm,
    formData,
    formFields,
    currentFormMessageId,
    setupForm,
    handleFormInputChange,
    createFormMessage,
    closeForm
  } = useInformationForm();
  
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [quotedMessage, setQuotedMessage] = useState(null);
  const [quotedMessageId, setQuotedMessageId] = useState(null);
  
  // Recent context management
  const {
    recentContexts,
    isPanelVisible,
    addContext,
    removeContext,
    togglePanel,
    createQuoteContext
  } = useRecentContext();
  
  const user = useSelector((state) => state.auth.user);

  const handleMessageSelection = (id) => {
    setSelectedMessageId((prev) => (prev === id ? null : id));
  };

  const handleQuote = (message) => {
    setQuotedMessage(message);
    setQuotedMessageId(message.id);
    // Add to recent context when quoted
    addContext(message);
  };

  const handleQuoteFromRecentContext = (context) => {
    const message = createQuoteContext(context);
    setQuotedMessage(message);
    setQuotedMessageId(message.id);
  };

  const handleRemoveQuote = () => {
    setQuotedMessage(null);
    setQuotedMessageId(null);
  };

  const handleFollowUpIntent = (followUpIntent, messageId, messageText) => {
    if (isProcessingChoice) {
      return;
    }
    
    setIsProcessingChoice(true);
    
    // Store intent to show confirmation in message bubble
    setMessageFollowUpIntents(prev => ({
      ...prev,
      [messageId]: followUpIntent
    }));
    
    if (followUpIntent === FOLLOW_UP_INTENTS.PROVIDE) {
      handleProvideIntent(messageId, messageText);
    } else if (followUpIntent === FOLLOW_UP_INTENTS.ASK_ELSE) {
      handleAskElseIntent();
    }
    
    // Prevent double-clicks during processing
    setTimeout(() => {
      setIsProcessingChoice(false);
    }, PROCESSING_TIMEOUT);
  };

  const handleProvideIntent = (messageId, messageText) => {
    setIsFollowupRequired(true);
    setupForm(messageId, messageText);
  };

  const handleAskElseIntent = () => {
    // Clear LangGraph state to start fresh conversation
    setDialogId(null);
    setThreadId(null);
    setIsFollowupRequired(false);
    
    // Reset the response flag so next request won't send dialog_id
    hasReceivedResponseRef.current = false;
    
    // Clear sessionStorage for this chatMode to start new conversation
    clearConversationStorage(chatMode);
    
    // Add acknowledgment message
    const acknowledgmentMessage = {
      id: Date.now(),
      sender: MESSAGE_SENDER_TYPES.BOT,
      message: FOLLOW_UP_UI_TEXT.CONTEXT_CHANGED_REPLY_MESSAGE,
      timestamp: fnCurrentTimeStamp(),
    };
    setMessages((prev) => [...prev, acknowledgmentMessage]);
  };

  const handleFormSubmit = () => {
    const formMessage = createFormMessage();
    
    if (!formMessage) {
      closeForm();
      return;
    }
    
    closeForm();
    handleSend(formMessage, 'text');
  };

  const handleFormCancel = () => {
    closeForm();
    };
    
    const formattedMode = chatMode.replace(/_/g, "-");
    const API_URL = `${process.env.REACT_APP_API_URL}/${formattedMode}`;


  const handleSend = async (message, input_type) => {
    // Process message data to extract user query and quoted content
    const { userQuery, quotedContent, isQuoted } = processMessageData(message);
    
    // Get system context from props and localStorage
    const username = localStorage.getItem('LoginUser_name') || '';
    const systemContext = getSystemContext({ companyCode, regionCode, userCode, username });
     
    // Build structured input prompt with clear sections
    const enhancedMessage = buildStructuredInputPrompt({
      userQuery,
      quotedContent,
      systemContext
    });
    
    // Check if this is a simple greeting that can be handled locally
      const { shouldHandleLocally, response: localResponse } = greetingHandler.shouldHandleLocally(userQuery, chatMode);
    
    // Keep original message structure for display
    const messageForDisplay = message;

    const newUserMessage = {
      id: Date.now(),
      sender: MESSAGE_SENDER_TYPES.USER,
      message: messageForDisplay, // Store structured data for display
      message_type: input_type,
      timestamp: fnCurrentTimeStamp(),
    };
    setMessages((prev) => [...prev, newUserMessage]);
    
    // Clear quote immediately when message is sent
    setQuotedMessage(null);
    setQuotedMessageId(null);
    
    // Handle simple greetings locally without API call
    if (shouldHandleLocally && !isQuoted) {
      // Add a small delay to simulate natural response timing
      setTimeout(() => {
        const botResponse = {
          id: Date.now() + 1,
          sender: MESSAGE_SENDER_TYPES.BOT,
          message: localResponse,
          timestamp: fnCurrentTimeStamp(),
          isLocalResponse: true, // Flag to indicate this was handled locally
        };
        setMessages((prev) => [...prev, botResponse]);
      }, 300); // Small delay for natural feel
      return;
    }
     
    setIsLoading(true);
    try {
      const metadata = {
        instance_id: chatModesMap[chatMode]?.instance_id || user?.instance_id  ,
        application_id: chatModesMap[chatMode]?.application_id,
        first_time_login: true,
        last_active_date_time: new Date().toISOString(),
        modal_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        is_followup_request: isFollowupRequired,
        user_id: String(user?.user_id || 0),
        // Only send dialog_id if we've received a response in this session
        // After page refresh, first request should have dialog_id: null
        dialog_id: (hasReceivedResponseRef.current && dialogId) ? dialogId : null,
      };

      // Include thread_id if available and we've received a response
      if (hasReceivedResponseRef.current && threadId) {
        metadata["thread_id"] = threadId;
      }

      let request_body;
      let config = {};
       
      if (message instanceof FormData) {
        message.append("type", input_type);
        message.append("metadata", JSON.stringify(metadata));
        request_body = message;
        config.headers = {
          "Content-Type": "multipart/form-data",
        };
      } else {
         
        request_body = {
          input_prompt: enhancedMessage,
          type: input_type,
          metadata,
        };
        config.headers = {
          "Content-Type": "application/json",
        };
      }

      const { data } = await axios.post(API_URL, request_body, config);

      if (data && data.success) {
        const response = data.data;
        
        // Mark that we've received a response in this session
        // This allows us to send dialog_id in subsequent requests
        hasReceivedResponseRef.current = true;
        
        // Always store dialog_id from response (regardless of followup_required)
        // This ensures conversation continuity across all requests
        if (response.dialog_id) {
          setDialogId(response.dialog_id);
          saveDialogIdToStorage(chatMode, response.dialog_id);
        }
        
        // Store thread_id if provided
        if (response.thread_id) {
          setThreadId(response.thread_id);
          saveThreadIdToStorage(chatMode, response.thread_id);
        }
        
        setIsFollowupRequired(response.followup_required);
        const fullUserMessage = {
          ...newUserMessage,
          instance_id: metadata.instance_id,
          dialog_id: response.dialog_id,
          application_id: metadata.application_id,
          user_id: metadata.user_id,
        };
        await conversationService.conversation(fullUserMessage);

        const botResponse = {
          id: Date.now() + 1,
          sender: MESSAGE_SENDER_TYPES.BOT,
          message: response.response || response.toString(),
          timestamp: fnCurrentTimeStamp(),
          image_id: response.image_id,
          table_id: response.table_id,
          application_id: metadata.application_id,
          dialog_id: response.dialog_id,
          instance_id: metadata.instance_id,
          user_id: metadata.user_id,
          showChoice: response.followup_required, // Add choice flag to message
          debug_info: response.debug_info, // Add debug info to message
        };
        setMessages((prev) => [...prev, botResponse]);
        await conversationService.conversation(botResponse);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        sender: MESSAGE_SENDER_TYPES.BOT,
        message: ERROR_MESSAGE,
        timestamp: fnCurrentTimeStamp(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      console.error("Chat API Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset response flag when chatMode changes (new conversation context)
  useEffect(() => {
    // When chatMode changes, reset the flag so first request in new mode won't send dialog_id
    hasReceivedResponseRef.current = false;
    // Also clear state to start fresh for new chatMode
    setDialogId(null);
    setThreadId(null);
  }, [chatMode]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    };

    scrollToBottom();
  }, [messages]);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-900 border-gray-300 dark:border-gray-600 border-2 transition-colors">
      <main
        ref={chatContainerRef}
        className="flex-grow overflow-y-auto overflow-x-hidden max-w-full p-4 space-y-2 flex flex-col chat-scroll-padding scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
      >
        <AnimatePresence>
          {messages.map((msg) => (
            <ChatBubble
              key={msg.id}
              data={msg}
              selectedMessageId={selectedMessageId}
              onSelect={handleMessageSelection}
              onQuote={handleQuote}
              quotedMessageId={quotedMessageId}
              imageId={msg.image_id}
              tableId={msg.table_id}
              dialogId={msg.dialog_id}
              onProvideIntent={() => handleFollowUpIntent(FOLLOW_UP_INTENTS.PROVIDE, msg.id, msg.message)}
              onAskSomethingElse={() => handleFollowUpIntent(FOLLOW_UP_INTENTS.ASK_ELSE, msg.id, msg.message)}
              followUpIntentExpressed={!!messageFollowUpIntents[msg.id]}
              userFollowUpIntent={messageFollowUpIntents[msg.id]}
              isProcessingChoice={isProcessingChoice}
            />
          ))}
          {isLoading && <ThinkingIndicator />}
        </AnimatePresence>
      </main>
      {/* Input Area with Recent Context Panel at Bottom */}
      <div className="relative w-full max-w-full chat-input-sticky bg-white/90 dark:bg-gray-800/90 backdrop-blur safe-bottom">
        <RecentContextPanel
          recentContexts={recentContexts}
          onQuoteContext={handleQuoteFromRecentContext}
          onRemoveContext={removeContext}
          isVisible={isPanelVisible}
          onToggle={togglePanel}
        />
        <InputBar 
          onSend={handleSend} 
          disabled={isLoading} 
          quotedMessage={quotedMessage}
          onRemoveQuote={handleRemoveQuote}
          onShowRecentContext={togglePanel}
          recentContextCount={recentContexts.length}
        />
      </div>
      
      <InformationForm
        showForm={showProvideForm}
        formFields={formFields}
        formData={formData}
        onInputChange={handleFormInputChange}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
      />
    </div>
  );
};
ChatBot.propTypes = {
    chatMode: PropTypes.string.isRequired,
    chatModesMap: PropTypes.object,
    companyCode: PropTypes.string,
    regionCode: PropTypes.string,
    userCode: PropTypes.string,
};


export default ChatBot;
